import { companiesForAutocompleteSchema } from "@/components/Forms/schema/companiesForAutocompleteSchema.ts";
import { emailAddressSchema } from "@/components/Forms/schema/emailAddressSchema.ts";
import { phoneNumberSchema } from "@/components/Forms/schema/phoneNumberSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl, emptyResponse } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createResourceCollectionSelector } from "jsonapi-zod-query";

const companyTypeSelector = createResourceCollectionSelector({
    type: "company",
    attributesSchema: companiesForAutocompleteSchema,
    relationships: {
        phoneNumbers: {
            relationshipType: "many",
            include: {
                type: "phoneNumber",
                attributesSchema: phoneNumberSchema,
            },
            required: false,
        },
        emailAddresses: {
            relationshipType: "many",
            include: {
                type: "emailAddress",
                attributesSchema: emailAddressSchema,
            },
            required: false,
        },
    },
});

export type Company = ReturnType<typeof companyTypeSelector>;

type Props = {
    searchString: string;
    fieldIdValue: string | null;
    isQueryEnabled?: boolean;
    subQueryKey?: string;
};

export const useFetchCompaniesForAutocomplete = ({
    searchString,
    fieldIdValue,
    isQueryEnabled = true,
    subQueryKey = "company-autocomplete",
}: Props): UseQueryResult<Company> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: [searchString, fieldIdValue, subQueryKey],
        enabled: isQueryEnabled,
        queryFn: async () => {
            if (!isQueryEnabled) {
                return emptyResponse;
            }

            const fetchURL = apiUrl("/company/search");

            const response = await fetch(fetchURL.href, {
                method: "POST",
                headers: {
                    "Content-Type": "application/vnd.api+json",
                },
                body: JSON.stringify({
                    searchString: searchString,
                    fieldIdValue: fieldIdValue,
                }),
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch companies: ${response.status}`);
            }

            return await response.json();
        },
        select: companyTypeSelector,
    });
};
